import React, { useState, useEffect } from 'react';
import { getGoalProgress, GoalProgressData } from '../../services/api';

interface DailyGoalWidgetProps {
  studentId: string;
}

export const DailyGoalWidget: React.FC<DailyGoalWidgetProps> = ({ studentId }) => {
  const [data, setData] = useState<GoalProgressData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGoalProgress(studentId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading || !data || !data.goals) return null;

  const { goals, progress, met } = data;
  if (!goals.questions_per_day && !goals.minutes_per_day && !goals.accuracy_target) return null;
  if (!progress || !met) return null;

  const allMet = [
    goals.questions_per_day ? met.questions_met : true,
    goals.minutes_per_day ? met.minutes_met : true,
    goals.accuracy_target ? met.accuracy_met : true,
  ].every(Boolean);

  const renderMiniGoal = (
    label: string,
    current: number,
    target: number,
    isMet: boolean,
    unit: string
  ) => {
    const pct = Math.min((current / target) * 100, 100);
    return (
      <div className="mini-goal" key={label}>
        <div className="mini-goal-header">
          <span className="mini-goal-label">{label}</span>
          <span className={`mini-goal-value ${isMet ? 'met' : ''}`}>
            {Math.round(current)}{unit}/{target}{unit}
          </span>
        </div>
        <div className="mini-goal-bar">
          <div
            className={`mini-goal-fill ${isMet ? 'complete' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`daily-goal-widget ${allMet ? 'all-met' : ''}`}>
      <div className="widget-header">
        <h4>Today's Goals {allMet && <span className="all-done-badge">All Done!</span>}</h4>
      </div>
      <div className="mini-goals">
        {goals.questions_per_day != null && renderMiniGoal(
          'Questions', progress.questions_today, goals.questions_per_day, met.questions_met, ''
        )}
        {goals.minutes_per_day != null && renderMiniGoal(
          'Time', progress.minutes_today, goals.minutes_per_day, met.minutes_met, 'm'
        )}
        {goals.accuracy_target != null && renderMiniGoal(
          'Accuracy', progress.accuracy_today, goals.accuracy_target, met.accuracy_met, '%'
        )}
      </div>
    </div>
  );
};
